/**
 * Acrostic poem helpers. Honest templates, no AI: the suggestions are hand-written word
 * lists grouped by theme, filtered by first letter. Nothing here generates text.
 */

export type ThemeId =
  | "general"
  | "nature"
  | "ocean"
  | "love"
  | "friendship"
  | "family"
  | "home"
  | "winter"
  | "spring"
  | "summer"
  | "autumn"
  | "school"
  | "courage"
  | "celebration"
  | "night"
  | "gratitude";

export const THEME_IDS: readonly ThemeId[] = [
  "general",
  "nature",
  "ocean",
  "love",
  "friendship",
  "family",
  "home",
  "winter",
  "spring",
  "summer",
  "autumn",
  "school",
  "courage",
  "celebration",
  "night",
  "gratitude",
];

/** Words that suit almost any acrostic, at least six for every letter. */
const GENERAL =
  "alive amber adore ablaze always awake abundant " +
  "bright brave bloom beyond breathe beautiful balance " +
  "calm clear cherish courage colour curious comfort " +
  "dream dawn delight dance deep dazzling devoted " +
  "eager endless embrace echo enchanted evergreen ever " +
  "free flourish fearless forever fragrant fresh fondly " +
  "gentle glow grace golden gather grateful glimmer " +
  "hope heart harmony home happy hush heaven " +
  "imagine inspire iris island infinite invite incredible " +
  "joy journey jubilant jasmine jewel joyful jaunty " +
  "kind kindle keen keepsake kinship kiss kite " +
  "love light laughter linger lively luminous lantern " +
  "magic melody morning moonlight marvel meadow mirth " +
  "new nurture noble nestle near nimble nightfall " +
  "open ocean overflow ode oasis optimism offer " +
  "peace pure playful promise petal passion praise " +
  "quiet quest quick quill quilt quench quaint " +
  "radiant rise rain rejoice river roam rhythm " +
  "shine sunrise soar spirit serene smile sparkle " +
  "treasure tender twilight trust together tranquil thrive " +
  "unity uplift unbroken universe unfold upward unique " +
  "vivid voyage velvet valiant vibrant verse vow " +
  "wonder warm whisper wisdom wild wander welcome " +
  "xenial xanadu xylophone xylem xenon xebec " +
  "young yearn yellow yes yonder yield yule " +
  "zeal zest zenith zephyr zen zigzag zinnia";

const THEMES: Readonly<Record<Exclude<ThemeId, "general">, string>> = {
  nature:
    "acorn aspen autumn bloom blossom branch breeze brook bud canopy cedar cliff clover creek dandelion dew dusk ember fern field fir forest fox frost glade grove harvest hawk heather hill ivy juniper lake leaf lichen maple meadow moss mountain nest oak orchard owl petal pine pond prairie quail rain river robin root sage shade soil sparrow spring stream sunlight thicket thistle timber trail tulip valley vine violet willow wren wildflower yarrow zephyr",
  ocean:
    "anchor atoll beach breaker brine buoy cove coral crest current dolphin driftwood dune eel estuary foam gull harbor horizon inlet island jellyfish kelp lagoon lighthouse mariner mermaid mist narwhal octopus pearl pier porpoise quay reef ripple sail salt sand seashell shore starfish surf swell tide trawler undertow voyage wave whale wind yacht zephyr",
  love: "adore affection amour beloved blush bliss cherish closeness devotion darling embrace endear fond forever gentle heart hold honey infinite intimate joy kiss kindred lasting longing lover melody mine nuzzle nurture oath only passion promise radiant romance soulmate sweetheart tender touch trust union valentine vow warmth wholehearted yearning yours zeal",
  friendship:
    "ally always buddy bond care chum companion confide cheer comrade dependable encourage equal faithful fun gather generous giggle honest hug humor inseparable joke kind laugh loyal memories mates neighbour open partner pal playmate quirky reliable share smile support team trust understanding unity visit warm welcome yarn zany",
  family:
    "ancestors aunt baby bedtime brother cousin cuddle dad daughter dinner elders embrace family father generations grandma grandpa grandchild hearth heritage home hug husband inherit joy kin kitchen laughter legacy mother mom nephew niece nurture parents quilt reunion roots sister siblings son stories supper traditions twin uncle unity vacation wife welcome yesterday zest",
  home: "apartment attic balcony basement bed blanket bookshelf brick candle chair chimney cottage couch cozy curtain door doorstep dwell fireplace floor garden gate hallway hearth kettle kitchen lamp lantern lullaby mantel mat neighbourhood nook oven pantry porch quilt roof rug shelter sofa staircase stove threshold table umbrella vase veranda window welcome yard",
  winter:
    "avalanche blanket blizzard breath cabin chill cocoa cold crystal december drift evergreen fireside flurry frost frozen glacier glove hibernate hearth hush icicle igloo january jacket knit lantern mitten north ornament penguin quiet sleigh scarf shiver skate sled snowflake solstice stillness storm thaw tundra twinkle umbrella velvet wonderland white wool woolen yule zero",
  spring:
    "april awaken bird blossom bloom bud butterfly cherry chick crocus daffodil dawn dew earth egg fresh garden grow greenery hatch hope iris jonquil kite lamb lilac march may meadow mud nest new orchard petal puddle rain rebirth robin seed shower sprout sunshine tulip umbrella violet warm wake wildflower yellow zeal",
  summer:
    "apricot barefoot beach berry breeze bonfire camp cicada cool daylight dragonfly fireflies freedom grill hammock heat holiday ice jump june july kayak lake lemonade lazy lightning melon nap ocean picnic pool popsicle quiet sandcastle sprinkler sunburn sunflower sunshine swim tan thunder umbrella vacation watermelon wave wade yacht zest",
  autumn:
    "acorn amber apple bonfire bronze cider cinnamon cozy crisp crimson fall foliage golden gourd harvest hayride hearth leaves maple mist mushroom nuts october orchard pumpkin quilt rake russet rustle scarecrow scarf spice squirrel sweater thanksgiving twilight umber wind walnut woodsmoke yield",
  school:
    "abacus alphabet answer art book chalk class classroom curious desk discover dictionary education essay experiment explore focus geography grammar history homework idea imagine journal knowledge library lesson logic math notebook observe paper pencil question quiz read reason science study teacher theory understand university vocabulary wisdom wonder write xylophone yearbook zeal",
  courage:
    "adventure audacity bold brave bravery bravo challenge conquer courage dare determined endure fearless fierce fight fortitude grit grow hero heart hope inspire invincible journey keep leap lion mighty noble overcome persevere power proud quest resolve rise spirit stand strength steadfast stronger trust triumph unafraid unbroken valiant victory vigor warrior will yes zeal",
  celebration:
    "applause balloon banner birthday blow bubble candle carnival cake cheer confetti dance delight dessert exciting festival fireworks fun gift giggle glitter grin happy hooray jolly jubilee kazoo laughter lantern merry music novelty party present parade quirky revel smile sparkle streamers surprise toast treat unwrap vivid wish wonder xylophone yippee zest zing",
  night:
    "aurora astral bedtime comet constellation crescent dark dream dusk eclipse evening firefly galaxy glow gleam hush hoot lantern lullaby midnight meteor moon moonbeam nebula night nocturne orbit owl planet quiet quasar rest silent silver sleep star starlight stillness telescope twilight twinkle universe velvet vast wish wonder yawn zenith zodiac",
  gratitude:
    "abundance appreciate blessed blessing bounty cherish count gift generous gracious grateful give glad heartfelt honour kindness light mindful noticing open overflow praise precious present quiet receive remember return share sincere thanks thankful treasure thoughtful unity uplift value warm welcome wonder yes yield zeal",
};

const RE_SPACE = /\s+/;

function wordsOf(list: string): string[] {
  return list.split(RE_SPACE).filter((word) => word.length > 0);
}

/** The words for a theme: its own list, then the general one, without repeats. */
export function themeWords(theme: ThemeId): string[] {
  const own = theme === "general" ? [] : wordsOf(THEMES[theme]);
  return [...new Set([...own, ...wordsOf(GENERAL)])];
}

/** Words that start with `letter`, the theme's own words first. */
export function suggestionsFor(theme: ThemeId, letter: string): string[] {
  const lower = letter.toLowerCase();
  return themeWords(theme).filter((word) => word.startsWith(lower));
}

export function capitalize(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/* ---------------------------------------------------------------- the word */

export interface AcrosticLetter {
  letter: string;
  /** True when a space came before this letter, so the poem starts a new stanza. */
  gapBefore: boolean;
}

const RE_COMBINING = /\p{M}/gu;
const RE_LATIN = /^[a-z]$/;
export const MAX_LETTERS = 30;

export interface ParsedWord {
  letters: AcrosticLetter[];
  /** Characters dropped because they are not letters a–z (after removing accents). */
  ignored: string[];
  tooLong: boolean;
}

/** "Alice Liddell" → A L I C E / L I D D E L L, with a gap before the second word. */
export function parseWord(input: string): ParsedWord {
  const letters: AcrosticLetter[] = [];
  const ignored: string[] = [];
  let gap = false;
  const stripped = input.normalize("NFD").replace(RE_COMBINING, "");
  for (const ch of stripped) {
    const lower = ch.toLowerCase();
    if (RE_LATIN.test(lower)) {
      letters.push({
        letter: lower.toUpperCase(),
        gapBefore: gap && letters.length > 0,
      });
      gap = false;
    } else if (ch.trim() === "" || ch === "-") {
      gap = true;
    } else if (!ignored.includes(ch)) {
      ignored.push(ch);
    }
  }
  return {
    letters: letters.slice(0, MAX_LETTERS),
    ignored,
    tooLong: letters.length > MAX_LETTERS,
  };
}

/* ----------------------------------------------------------------- the lines */

/** True when a line begins with its letter (ignoring case and leading space). */
export function startsWithLetter(line: string, letter: string): boolean {
  return line.trim().charAt(0).toUpperCase() === letter.toUpperCase();
}

/** Keep the lines whose letter has not changed when the word is edited. */
export function carryLines(
  previous: readonly string[],
  before: readonly AcrosticLetter[],
  after: readonly AcrosticLetter[]
): string[] {
  return after.map((entry, i) =>
    before[i]?.letter === entry.letter ? (previous[i] ?? "") : ""
  );
}

/**
 * One suggestion per letter, chosen at random from the theme's words for it.
 * `random` returns a float in [0, 1); the widget passes a crypto-backed one on a button
 * press, tests pass a fixed one.
 */
export function fillLines(
  letters: readonly AcrosticLetter[],
  theme: ThemeId,
  random: () => number
): string[] {
  const used = new Set<string>();
  return letters.map((entry) => {
    const options = suggestionsFor(theme, entry.letter).filter(
      (word) => !used.has(word)
    );
    if (options.length === 0) {
      return entry.letter;
    }
    const word = options[Math.floor(random() * options.length)];
    used.add(word);
    return capitalize(word);
  });
}

/** The poem as plain text, stanza breaks where the word had a space. */
export function poemText(
  letters: readonly AcrosticLetter[],
  lines: readonly string[]
): string {
  const out: string[] = [];
  for (const [i, entry] of letters.entries()) {
    if (entry.gapBefore) {
      out.push("");
    }
    const line = (lines[i] ?? "").trim();
    out.push(line.length > 0 ? line : entry.letter);
  }
  return out.join("\n");
}

const ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
};
const RE_HTML = /[&<>"]/g;

export function escapeHtml(text: string): string {
  return text.replace(RE_HTML, (ch) => ESCAPES[ch]);
}

/** A standalone page for printing: title, then the lines with the first letter bold. */
export function printPage(
  title: string,
  letters: readonly AcrosticLetter[],
  lines: readonly string[]
): string {
  const body = letters
    .map((entry, i) => {
      const raw = (lines[i] ?? "").trim() || entry.letter;
      const rest = escapeHtml(raw.slice(1));
      const first = escapeHtml(raw.charAt(0));
      const gap = entry.gapBefore ? '<div class="gap"></div>' : "";
      return `${gap}<p class="line"><b>${first}</b>${rest}</p>`;
    })
    .join("");
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>@page{margin:2cm}body{font-family:Georgia,'Times New Roman',serif;color:#111;margin:0}h1{font-size:22pt;text-align:center;margin:0 0 1.2em;font-weight:normal;letter-spacing:.05em}.line{font-size:16pt;line-height:1.6;margin:0}.line b{font-size:20pt}.gap{height:1em}</style></head><body><h1>${escapeHtml(title)}</h1>${body}</body></html>`;
}
